import { useEffect, useState, useCallback } from 'react';
import { OverviewMetrics, Metric, LogEntry, ActiveProcess } from '../types';
import api from '../services/api';
import MetricsOverview from '../components/MetricsOverview';
import EndpointMetrics from '../components/EndpointMetrics';
import ErrorLogs from '../components/ErrorLogs';
import SuccessLogs from '../components/SuccessLogs';
import ActiveUsers from '../components/ActiveUsers';
import ActiveProcesses from '../components/ActiveProcesses';
import { RefreshCw } from 'lucide-react';

export default function Dashboard() {
  const [overview, setOverview] = useState<OverviewMetrics | null>(null);
  const [endpoints, setEndpoints] = useState<Metric[]>([]);
  const [errors, setErrors] = useState<LogEntry[]>([]);
  const [successes, setSuccesses] = useState<LogEntry[]>([]);
  const [activeProcesses, setActiveProcesses] = useState<ActiveProcess[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState('24h');

  const fetchData = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const [overviewRes, endpointsRes, errorsRes, successesRes, activeRes] = await Promise.all([
        api.get(`/metrics/overview?period=${period}`),
        api.get(`/metrics/endpoints?period=${period}`),
        api.get(`/metrics/errors?period=${period}&limit=10`),
        api.get(`/metrics/successes?period=${period}&limit=10`),
        api.get('/metrics/active?limit=50'),
      ]);

      setOverview(overviewRes.data?.data ?? null);
      setEndpoints(Array.isArray(endpointsRes.data?.data) ? endpointsRes.data.data : []);
      setErrors(Array.isArray(errorsRes.data?.data) ? errorsRes.data.data : []);
      setSuccesses(Array.isArray(successesRes.data?.data) ? successesRes.data.data : []);
      setActiveProcesses(Array.isArray(activeRes.data?.data) ? activeRes.data.data : []);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period]);

  const fetchActiveProcesses = useCallback(async () => {
    try {
      const res = await api.get<{ data?: ActiveProcess[] }>('/metrics/active?limit=50');
      setActiveProcesses(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (error) {
      console.error('Error fetching active processes:', error);
    }
  }, []);

  const handleAbortProcess = useCallback(async (requestLogId: string) => {
    await api.post(`/metrics/requests/${requestLogId}/abort`);
    await fetchActiveProcesses();
  }, [fetchActiveProcesses]);

  const handleRefresh = () => {
    fetchData(true);
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(), 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(fetchActiveProcesses, 10000); // Processos em execução a cada 10s
    return () => clearInterval(interval);
  }, [fetchActiveProcesses]);

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary-600">Carregando métricas...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold text-gray-900">Monitoramento em Tempo Real</h2>
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Atualizar dados"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? 'Atualizando...' : 'Atualizar'}</span>
          </button>
        </div>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white"
          aria-label="Período das métricas"
        >
          <option value="1h">Última hora</option>
          <option value="24h">Últimas 24 horas</option>
          <option value="7d">Últimos 7 dias</option>
        </select>
      </div>

      {overview && <MetricsOverview metrics={overview} />}

      <section aria-label="Processos em execução">
        <ActiveProcesses processes={activeProcesses} onAbort={handleAbortProcess} />
      </section>

      {endpoints.length > 0 && <EndpointMetrics endpoints={endpoints} />}

      <SuccessLogs successes={successes} />
      <ErrorLogs errors={errors} />
      <ActiveUsers period={period} />
    </div>
  );
}

